import { getVariant } from '@chessbut/engine';
import type { GameRecordSummary, RatingSummary } from '@chessbut/shared';
import { useEffect, useState } from 'react';
import { Avatar } from '../components/Brand.tsx';
import { api } from '../lib/api.ts';
import { relativeTime } from '../lib/format.ts';
import { Link } from '../lib/router.tsx';
import { useSession } from '../lib/session.ts';
import { NotFound } from './Static.tsx';

interface Profile {
  player: {
    id: string;
    name: string;
    username: string;
    createdAt: number;
    ratings: Record<string, RatingSummary>;
    streak: number;
    bestStreak: number;
    today: number;
  };
  games: GameRecordSummary[];
}

export function ProfilePage({ username }: { username: string }) {
  const [data, setData] = useState<Profile | null>(null);
  const [missing, setMissing] = useState(false);
  const { me } = useSession();
  useEffect(() => {
    setData(null);
    setMissing(false);
    api<Profile>(`/api/players/${encodeURIComponent(username)}`)
      .then(setData)
      .catch(() => setMissing(true));
  }, [username, me?.today.points]);
  if (missing) return <NotFound title="No such player" />;
  if (!data) return <div className="page loading"><span className="spinner big" /></div>;
  const { player, games } = data;
  const pools = Object.values(player.ratings).sort((a, b) => (a.pool === 'overall' ? -1 : b.pool === 'overall' ? 1 : b.games - a.games));
  const overall = player.ratings.overall;
  const isMe = me?.id === player.id;
  return (
    <div className="page">
      <section className="profile-head">
        <Avatar name={player.username} size={56} />
        <div className="grow">
          <h1 className="page-title">{player.username}</h1>
          <p className="muted small">Joined {new Date(player.createdAt).toLocaleDateString()}</p>
        </div>
        {isMe && <span className="you-tag">you</span>}
      </section>
      <section className="stats">
        <div className="stat">
          <div className="k">Rating</div>
          <div className="v">
            {overall ? overall.rating : '–'}
            {overall?.provisional && <span className="q">?</span>}
          </div>
        </div>
        <div className="stat">
          <div className="k">Streak</div>
          <div className="v">
            {player.streak}
            <small>best {player.bestStreak}</small>
          </div>
        </div>
        <div className="stat">
          <div className="k">Today</div>
          <div className="v">
            {player.today}
            <small>pts</small>
          </div>
        </div>
      </section>
      {pools.length > 0 && (
        <section className="group">
          <h2 className="section-title">Ratings</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Variant</th>
                <th>Rating</th>
                <th>Games</th>
                <th>W / L / D</th>
              </tr>
            </thead>
            <tbody>
              {pools.map((r) => (
                <tr key={r.pool}>
                  <td>{r.pool === 'overall' ? <b>Overall</b> : <Link to={`/${r.pool}`}>{getVariant(r.pool)?.info.name ?? r.pool}</Link>}</td>
                  <td>
                    <b>{r.rating}</b>
                    {r.provisional ? '?' : ''} <span className="muted small">best {r.best}</span>
                  </td>
                  <td>{r.games}</td>
                  <td>
                    {r.wins} / {r.losses} / {r.draws}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      <section className="group">
        <h2 className="section-title">Recent games</h2>
        {games.length ? <GameList games={games} playerId={player.id} /> : <p className="empty">No finished games yet.</p>}
      </section>
    </div>
  );
}

export function GameList({ games, playerId }: { games: GameRecordSummary[]; playerId: string }) {
  return (
    <ul className="list game-list">
      {games.map((g) => {
        const white = g.white.id === playerId;
        const me = white ? g.white : g.black;
        const opp = white ? g.black : g.white;
        const outcome = g.result === '1/2-1/2' ? 'draw' : (g.result === '1-0') === white ? 'win' : 'loss';
        const v = getVariant(g.variant);
        return (
          <li key={g.id}>
            <Link to={`/game/${g.id}`} className="list-row">
              <span className={`result-chip ${outcome}`} title={outcome === 'win' ? 'Won' : outcome === 'loss' ? 'Lost' : 'Drew'}>
                {outcome === 'win' ? '1' : outcome === 'loss' ? '0' : '½'}
              </span>
              <span className="grow">
                <span className="name">vs {opp.username ? opp.username : opp.name}</span>
                <span className="meta">
                  {v?.info.name ?? g.variant} · {g.mode === 'bot' ? 'vs bot' : g.rated ? 'rated' : 'casual'} · {Math.ceil(g.plies / 2)} moves
                </span>
              </span>
              <span className="game-side">
                {me.diff !== null && (
                  <span className={me.diff >= 0 ? 'good' : 'bad'}>
                    {me.diff >= 0 ? '+' : ''}
                    {me.diff}
                  </span>
                )}
                <span className="muted small">{relativeTime(g.endedAt ?? g.startedAt)}</span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
